import { UseFormReturn, useWatch } from "react-hook-form";
import { WizardSchemaType } from "..";
import React, { useMemo, useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	accordionSummaryClasses,
	Box,
	Button,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	Stack,
} from "@mui/material";
import TextMaxLine from "@onair/ui/text-max-line";
import { Iconify } from "@onair/minimal-ui/iconify";
import { StepTimelineSchemaType } from "../step-timeline";
import SortableEventSessionForm from "./sortable-event-session-form";
import { useResponsive } from "@onair/minimal-ui/hooks/use-responsive";
import EventSessionNumberBadge from "./event-session-number-badge";
import {
	LocationType,
	SessionPriceType,
	TicketScope,
} from "@onair/repositories";

interface Props {
	eventSession: StepTimelineSchemaType["sessions"][0] & { id: string };
	index: number;
	methods: UseFormReturn<WizardSchemaType>;
	onRemove: (index: number) => void;
}

const SortableEventSession = ({
	eventSession,
	index,
	methods,
	onRemove,
}: Props) => {
	const {
		control,
		formState: { errors },
		getValues,
	} = methods;
	const {
		attributes,
		isSorting,
		listeners,
		setNodeRef,
		transform,
		transition,
	} = useSortable({ id: eventSession.id });
	const mdDown = useResponsive("down", "md");
	const [expanded, setExpanded] = useState(false);

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
	};

	const eventSessionName = useWatch({
		name: `stepTimeline.sessions.${index}.name`,
		control,
	});

	const isError = Boolean(errors?.stepTimeline?.sessions?.[index]);
	const disableRemoveEventSession = useMemo<boolean>(() => {
		const priceType = getValues("stepPricing.priceType");
		const locationType = getValues("stepPricing.locationType");
		const ticketScope = getValues("stepPricing.ticketScope");
		const hasEntity = Boolean(getValues(`stepTimeline.sessions.${index}._id`));

		if (
			priceType === SessionPriceType.ALL &&
			locationType === LocationType.ONLINE
		) {
			return (
				getValues(`stepPricing.sessions.${index}.ticketTiers.0.hasBeenPaid`) &&
				hasEntity
			);
		}

		if (
			ticketScope === TicketScope.ALL &&
			locationType === LocationType.OFFLINE
		) {
			return getValues("stepPricing.ticketTiers")?.some(
				(ticketTier) => ticketTier.hasBeenPaid,
			);
		}

		const ticketTiers = getValues(`stepPricing.sessions.${index}.ticketTiers`);

		const hasBeenPaid = ticketTiers?.length
			? ticketTiers.some((ticketTier) => ticketTier.hasBeenPaid)
			: false;

		return hasBeenPaid && hasEntity;
	}, [getValues]);

	const header = useMemo(
		() => (
			<Stack direction="row" alignItems="center" spacing={1}>
				<EventSessionNumberBadge number={index + 1} />
				<TextMaxLine
					line={1}
					variant="subtitle2"
					lineHeight="20px"
					fontWeight={600}
				>
					{eventSessionName || "Tên sự kiện"}
				</TextMaxLine>
			</Stack>
		),
		[eventSessionName, index],
	);

	return (
		<Accordion
			ref={setNodeRef}
			expanded={expanded}
			onChange={() => setExpanded((prev) => !prev)}
			sx={{
				...style,
				mt: "0 !important",
				"&:before": {
					backgroundColor: "unset",
				},
				[`&.${accordionSummaryClasses.expanded}`]: {
					mb: (theme) => `0 !important`,
					boxShadow: {
						xs: "none",
						md: "0 8px 16px 0 rgba(145 158 171 / 0.16)",
					},
				},
			}}
		>
			<AccordionSummary
				sx={{
					backgroundColor: isError
						? "rgba(255, 86, 48, 0.12)"
						: "rgba(145, 158, 171, 0.08)",
					border: `1px solid ${isError ? "#F04438" : "transparent"}`,
					my: 0,
					px: 2,
					py: 1.5,
					borderRadius: isSorting ? "12px !important" : 1.5,
					minHeight: "48px !important",
					alignItems: {
						xs: "flex-start",
						md: "center",
					},
					[`& .${accordionSummaryClasses.content}`]: {
						my: (theme) => `0 !important`,
					},
					[`& .${accordionSummaryClasses.expanded}`]: {
						my: (theme) => `0 !important`,
					},
					[`&.${accordionSummaryClasses.expanded}`]: {
						borderRadius: "12px 12px 0 0 !important",
					},
				}}
				expandIcon={
					<Button
						variant="soft"
						sx={{
							backgroundColor: "white",
							width: 34,
							minWidth: "unset",
						}}
					>
						<Iconify
							color="action.active"
							width={18}
							icon="eva:arrow-ios-downward-fill"
						/>
					</Button>
				}
			>
				<Stack
					width="100%"
					direction="row"
					justifyContent="space-between"
					alignItems={{
						xs: "flex-start",
						md: "center",
					}}
				>
					<Stack
						direction={{
							xs: "column",
							md: "row",
						}}
						alignItems={{
							xs: "flex-start",
							md: "end",
						}}
						justifyContent={{
							xs: "flex-start",
							md: "space-between",
						}}
						spacing={1}
					>
						<Box {...listeners} {...attributes}>
							<Iconify
								color="text.secondary"
								icon="radix-icons:drag-handle-dots-2"
							/>
						</Box>
						{header}
					</Stack>
					<Stack direction="row" mr={1} spacing={1}>
						<Button
							variant="soft"
							disabled={disableRemoveEventSession}
							sx={{
								backgroundColor: "white",
								width: 36,
								minWidth: "unset",
							}}
							onClick={(event) => {
								event.stopPropagation();
								onRemove(index);
							}}
						>
							<Iconify
								color={
									disableRemoveEventSession
										? "action.disabled"
										: "action.active"
								}
								width={18}
								icon="solar:trash-bin-trash-bold"
							/>
						</Button>
					</Stack>
				</Stack>
			</AccordionSummary>
			{!mdDown && (
				<AccordionDetails sx={{ pt: 2, display: isSorting ? "none" : "block" }}>
					<SortableEventSessionForm index={index} methods={methods} />
				</AccordionDetails>
			)}
			{mdDown && (
				<Dialog
					fullScreen={true}
					open={expanded}
					onClose={() => setExpanded(false)}
				>
					<DialogTitle
						sx={{
							py: 2,
							px: 1,
						}}
					>
						<Stack
							direction="row"
							justifyContent="space-between"
							alignItems="center"
						>
							<IconButton onClick={() => setExpanded(false)} size="medium">
								<Iconify width={24} icon="eva:arrow-ios-back-fill" />
							</IconButton>
							<IconButton onClick={() => onRemove(index)} size="medium">
								<Iconify
									color="action.active"
									width={24}
									icon="solar:trash-bin-trash-bold"
								/>
							</IconButton>
						</Stack>
					</DialogTitle>
					<DialogContent
						sx={{
							p: 2,
							pb: 3,
						}}
					>
						<Box mb={2}>{header}</Box>
						<SortableEventSessionForm index={index} methods={methods} />
					</DialogContent>
				</Dialog>
			)}
		</Accordion>
	);
};

export default SortableEventSession;
